<template>
  <span class="kbd" :class="{ 'kbd--spaced': IS_MAC }">
    <template v-for="(cap, i) in caps" :key="i">
      <span v-if="i > 0 && !IS_MAC && !compact" class="kbdSep">+</span>
      <span :class="{ kbdGlyph: cap.glyph }">{{ cap.text }}</span>
    </template>
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { formatBinding, IS_MAC, type KeyCap } from '../composables/useHotkeys'

const props = defineProps<{ binding: string; compact?: boolean }>()

const COMPACT_SWAP: Record<string, KeyCap> = {
  Ctrl: { text: '^', glyph: true },
  Shift: { text: '⇧', glyph: true },
  Alt: { text: '⌥', glyph: true },
}

const caps = computed(() => {
  const raw = formatBinding(props.binding)
  if (!props.compact) return raw
  return raw.map((c) => COMPACT_SWAP[c.text] ?? c)
})
</script>

<style scoped>
.kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  min-width: var(--space-5);
  height: var(--space-5);
  padding: 0 var(--space-2);
  border-radius: var(--radius-sm);
  background: var(--color-bg-subtle);
  border: 1px solid var(--color-border-subtle);
  color: var(--color-text-secondary);
  font-family: var(--font-mono);
  font-size: var(--fs-xs);
  font-weight: var(--fw-medium);
  line-height: 1;
  transition: border-color var(--duration-fast) var(--ease-out),
              color var(--duration-fast) var(--ease-out);
}

/* Mac: separate parts of a combo cap so "⌘ Shift A" doesn't run together. */
.kbd--spaced { gap: 3px; }

/* Symbol glyphs (⌘ ⌥ ⌃ ⇧ ⏎ ↑ ↓ ←→) render smaller than letters in
   monospace. Bump font-size so the glyph matches adjacent letters. */
.kbdGlyph {
  font-size: 1.35em;
  line-height: 1;
  display: inline-block;
}
</style>
